import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { firePixel } from "../lib/pixel";
import { WHATSAPP_LINK } from "../i18n";

const SEGMENTS = [
  { label: "5% Bonus", color: "#0A0A0A" },
  { label: "Free Spin", color: "#161207" },
  { label: "7% Bonus", color: "#0A0A0A" },
  { label: "₹500", color: "#161207" },
  { label: "10% Cashback", color: "#0A0A0A" },
  { label: "20% Boost", color: "#161207" },
  { label: "VIP Access", color: "#0A0A0A" },
  { label: "₹1000", color: "#161207" },
];

// Rewards worth landing on (indices) — always a "good" outcome
const WIN_INDICES = [0, 2, 4, 5];

const R = 150;
const CX = 160;
const CY = 160;

function polar(angleDeg, radius) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
}

function slicePath(startAngle, endAngle) {
  const s = polar(startAngle, R);
  const e = polar(endAngle, R);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${CX} ${CY} L ${s.x} ${s.y} A ${R} ${R} 0 ${large} 1 ${e.x} ${e.y} Z`;
}

export const SpinWheel = ({ open, onClose, t }) => {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const seg = 360 / SEGMENTS.length;

  const handleSpin = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    const winIndex = WIN_INDICES[Math.floor(Math.random() * WIN_INDICES.length)];
    // segment center angle from top (clockwise)
    const centerAngle = winIndex * seg + seg / 2;
    const target = 360 * 6 + (360 - centerAngle);
    const finalRotation = rotation - (rotation % 360) + target;
    setRotation(finalRotation);
    setTimeout(() => {
      setSpinning(false);
      setResult(SEGMENTS[winIndex].label);
      firePixel("Lead", { content_name: "spin_reward", reward: SEGMENTS[winIndex].label });
    }, 4600);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="spin-modal"
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-md border border-gold/30 bg-surface p-8"
            initial={{ scale: 0.9, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 30 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
          >
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-white/50 hover:text-gold"
              data-testid="spin-close"
            >
              <X size={22} />
            </button>

            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
              Cric1x Rewards
            </p>
            <h3 className="mt-2 font-display text-4xl font-bold uppercase leading-none gold-text">
              {t.spin_title}
            </h3>
            <p className="mt-2 text-sm text-white/60">{t.spin_sub}</p>

            <div className="relative mx-auto mt-8 h-[320px] w-[320px]">
              {/* Pointer */}
              <div className="absolute left-1/2 top-[-6px] z-20 -translate-x-1/2">
                <div className="h-0 w-0 border-l-[12px] border-r-[12px] border-t-[22px] border-l-transparent border-r-transparent border-t-gold" />
              </div>
              <motion.svg
                width="320"
                height="320"
                viewBox="0 0 320 320"
                animate={{ rotate: rotation }}
                transition={{ duration: 4.6, ease: [0.16, 1, 0.3, 1] }}
                className="gold-glow"
              >
                {SEGMENTS.map((s, i) => {
                  const start = i * seg;
                  const end = start + seg;
                  const mid = start + seg / 2;
                  const tp = polar(mid, R * 0.62);
                  return (
                    <g key={i}>
                      <path d={slicePath(start, end)} fill={s.color} stroke="#D4AF37" strokeWidth="1" />
                      <text
                        x={tp.x}
                        y={tp.y}
                        fill="#F5D66E"
                        fontSize="12"
                        fontFamily="Space Mono, monospace"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${mid}, ${tp.x}, ${tp.y})`}
                      >
                        {s.label}
                      </text>
                    </g>
                  );
                })}
                <circle cx={CX} cy={CY} r="26" fill="#050505" stroke="#D4AF37" strokeWidth="2" />
                <text x={CX} y={CY} fill="#D4AF37" fontSize="10" fontFamily="Clash Display" fontWeight="700" textAnchor="middle" dominantBaseline="middle">1X</text>
              </motion.svg>
            </div>

            {result ? (
              <div className="mt-6 text-center" data-testid="spin-result">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
                  {t.spin_won}
                </p>
                <p className="mt-1 font-display text-3xl font-bold gold-text">{result}</p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-block w-full bg-wa py-4 text-center font-display text-sm font-semibold uppercase tracking-wide text-white transition-transform hover:scale-[0.98]"
                  data-testid="spin-claim-btn"
                >
                  {t.spin_claim}
                </a>
              </div>
            ) : (
              <button
                onClick={handleSpin}
                disabled={spinning}
                className="mt-6 w-full bg-gold py-4 font-display text-base font-bold uppercase tracking-widest text-black transition-transform hover:scale-[0.98] disabled:opacity-60"
                data-testid="spin-action-btn"
              >
                {spinning ? "..." : t.spin_btn}
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
